import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Ruler } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * Product carousel with color-swatch sync + Size Chart slide.
 *
 * Behaviour:
 *  - `images` array renders as slides.
 *  - Optional `sizeChartUrl` OR `sizeChartData` appends a size-chart slide at the end.
 *    - `sizeChartData`: [{size, ld, pb}, ...] renders a styled HTML table.
 *    - `sizeChartUrl`: string image URL renders as an image.
 *  - Clicking the "Size Chart" pill scrolls right to that final slide.
 *  - Clicking a swatch in `colors` scrolls to the slide with matching index
 *    (colors[0] → images[0], colors[1] → images[1], ...).
 */
export default function ProductCarousel({
  images = [],
  colors = [],
  sizeChartUrl = "",
  sizeChartData = null,
  alt = "Product",
  featured = false,
}) {
  const { t } = useI18n();
  const baseSlides = images.filter(Boolean).slice(0, 8);
  const hasSizeChartTable = Array.isArray(sizeChartData) && sizeChartData.length > 0;
  const hasSizeChartImage = Boolean(sizeChartUrl);
  const hasSizeChart = hasSizeChartTable || hasSizeChartImage;
  // We keep the sentinel string "__SIZE_CHART__" in the slides array when the chart is a
  // table so the carousel still counts slides correctly. Image URL is stored as-is.
  const slides = useMemo(() => {
    if (hasSizeChartTable) return [...baseSlides, "__SIZE_CHART__"];
    if (hasSizeChartImage) return [...baseSlides, sizeChartUrl];
    return baseSlides;
  }, [baseSlides, hasSizeChartTable, hasSizeChartImage, sizeChartUrl]);
  const sizeChartIndex = hasSizeChart ? slides.length - 1 : -1;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: slides.length > 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (i) => {
      if (!emblaApi) return;
      const max = slides.length - 1;
      emblaApi.scrollTo(Math.max(0, Math.min(i, max)));
    },
    [emblaApi, slides.length]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  if (slides.length === 0) return null;

  const single = slides.length === 1;
  const showingSizeChart = hasSizeChart && selectedIndex === sizeChartIndex;

  return (
    <div data-testid="product-carousel">
      <div className="relative group">
        <div className="overflow-hidden bg-stone-100 aspect-[4/5]" ref={emblaRef}>
          <div className="flex h-full">
            {slides.map((src, i) => (
              <div key={i} className="flex-[0_0_100%] min-w-0 h-full relative">
                {i === sizeChartIndex && hasSizeChartTable ? (
                  <SizeChartTable data={sizeChartData} t={t} />
                ) : (
                  <>
                    <img
                      src={src}
                      alt={i === sizeChartIndex ? `${alt} size chart` : `${alt} ${i + 1}`}
                      className={`w-full h-full transition-transform duration-700 group-hover:scale-[1.03] ${
                        i === sizeChartIndex ? "object-contain bg-white p-4" : "object-cover"
                      }`}
                      loading="lazy"
                    />
                    {i === sizeChartIndex && (
                      <div className="absolute top-3 left-3 bg-[#0A0A0A] text-white px-2.5 py-1 text-[10px] tracking-widest font-semibold">
                        SIZE CHART
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {featured && (
          <div className="absolute top-4 left-4 bg-[#F39237] text-[#0A0A0A] px-3 py-1 text-[10px] tracking-widest font-semibold z-10">
            BEST SELLER
          </div>
        )}

        {hasSizeChart && (
          <button
            type="button"
            onClick={() => scrollTo(sizeChartIndex)}
            data-testid="size-chart-button"
            aria-label="Show size chart"
            className={`absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-widest font-semibold transition-colors shadow-sm ${
              showingSizeChart
                ? "bg-[#F39237] text-[#0A0A0A]"
                : "bg-white/90 text-[#0A0A0A] hover:bg-[#F39237]"
            }`}
          >
            <Ruler size={12} />
            {t("catalog.size_chart", "Size Chart")}
          </button>
        )}

        {!single && (
          <>
            <button
              data-testid="carousel-prev"
              onClick={scrollPrev}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-[#0A0A0A] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="carousel-next"
              onClick={scrollNext}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-[#0A0A0A] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollTo(i)}
                  data-testid={`carousel-dot-${i}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    selectedIndex === i ? "bg-white w-4" : "bg-white/60"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {colors.length > 0 && (
        <ColorSwatches
          colors={colors}
          activeIndex={selectedIndex}
          onSelect={scrollTo}
        />
      )}
    </div>
  );
}

const COLOR_MAP = {
  merah: "#DC2626", maroon: "#7F1D1D", pink: "#EC4899", rose: "#F43F5E",
  hitam: "#0A0A0A", black: "#0A0A0A", putih: "#F5F5F4", white: "#F5F5F4",
  krem: "#EED9B0", cream: "#EED9B0", coklat: "#78350F", brown: "#78350F",
  navy: "#1E3A8A", biru: "#2563EB", blue: "#2563EB",
  hijau: "#16A34A", green: "#16A34A", olive: "#556B2F",
  kuning: "#EAB308", yellow: "#EAB308", mustard: "#D4A017",
  ungu: "#7C3AED", purple: "#7C3AED", lavender: "#B497D6",
  abu: "#78716C", grey: "#78716C", gray: "#78716C",
  peach: "#FFCBA4", mocha: "#4B2E2B",
};

function ColorSwatches({ colors, activeIndex, onSelect }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="product-colors">
      {colors.slice(0, 8).map((c, i) => {
        const norm = String(c || "").trim().toLowerCase();
        const bg = COLOR_MAP[norm] || "#D6D3D1";
        const active = i === activeIndex;
        return (
          <button
            key={`${c}-${i}`}
            type="button"
            onClick={() => onSelect(i)}
            data-testid={`color-swatch-${norm || i}`}
            aria-label={`Show ${c}`}
            title={c}
            className={`w-6 h-6 rounded-full border-2 transition-all ${
              active ? "border-[#F39237] scale-110 shadow" : "border-stone-300 hover:border-[#F39237]"
            }`}
            style={{ background: bg }}
          />
        );
      })}
      {colors[activeIndex] && (
        <span className="ml-2 text-xs text-stone-500 capitalize" data-testid="active-color-label">
          {colors[activeIndex]}
        </span>
      )}
    </div>
  );
}

function SizeChartTable({ data, t }) {
  return (
    <div
      className="w-full h-full bg-gradient-to-br from-[#FDFBF7] via-white to-[#F5F2EB] p-5 flex flex-col justify-center"
      data-testid="size-chart-table"
    >
      <div className="text-center mb-3">
        <div className="overline text-[#F39237] mb-1">{t("catalog.size_chart", "Size Chart")}</div>
        <div className="font-display text-lg md:text-xl text-[#0A0A0A]">Aurora Hijab</div>
        <div className="text-[10px] tracking-wider text-stone-500 mt-1">
          LD = Lingkar Dada · PB = Panjang Baju (cm)
        </div>
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[#0A0A0A] text-white">
            <th className="py-2 px-2 text-left text-xs tracking-widest">SIZE</th>
            <th className="py-2 px-2 text-right text-xs tracking-widest">LD</th>
            <th className="py-2 px-2 text-right text-xs tracking-widest">PB</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.size}
              className={i % 2 === 0 ? "bg-white" : "bg-[#F5F2EB]/60"}
            >
              <td className="py-2 px-2 font-display text-[#F39237] font-semibold">{row.size}</td>
              <td className="py-2 px-2 text-right font-mono">{row.ld}</td>
              <td className="py-2 px-2 text-right font-mono">{row.pb}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
